import React from "react";

// Article 5 — TORCH Tests in Pregnancy: Does IgG Positive Mean My Baby Is Infected?
// Content is verbatim from the approved brief. Do not paraphrase, shorten, expand
// or add FAQs, sticky contents menu, ratings, comments, popularity counters or
// unrelated call-to-action. Medical-review line is withheld.

function Cite({ n }) {
  const first = String(n).split(",")[0];
  return (
    <sup className="text-prestige font-medium">
      <a
        href={`#ref-${first}`}
        aria-label={`See reference ${first}`}
        className="hover:underline"
      >{`[${n}]`}</a>
    </sup>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-12">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] text-stone2 leading-relaxed">{children}</p>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
    <span>{children}</span>
  </li>
);

const Bullets = ({ items }) => (
  <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
    {items.map((i, idx) => (
      <Bullet key={idx}>{i}</Bullet>
    ))}
  </ul>
);

export function TorchIggIgmPositivePregnancy() {
  return (
    <article className="mt-10">
      <P>
        Usually, <strong>no</strong>. IgG positive most often means that the mother encountered that organism previously or developed antibodies after vaccination. It does not, by itself, prove a current infection—and it certainly does not prove that the baby is infected.
      </P>
      <P>
        IgM positive is not an automatic diagnosis of recent infection either. It may persist, cross-react or be falsely positive. The organism, antibody combination, timing, symptoms and confirmatory tests must be interpreted together.
      </P>

      <H2>TORCH Is Not One Disease and Not One Formula</H2>
      <P>“TORCH” is a label for several infections, and the exact panel varies between laboratories.</P>
      <P>The common mistake is to apply one rule to every result:</P>
      <Bullets
        items={[
          "IgG positive equals old infection and complete safety.",
          "IgM positive equals recent infection and fetal infection.",
        ]}
      />
      <P>Neither statement is universally correct.</P>
      <P>A maternal antibody result does not automatically establish when infection occurred or whether it crossed the placenta.</P>

      <H2>What Does IgG Positive Usually Mean?</H2>
      <P>IgG generally appears after exposure and may remain detectable for years or for life.</P>
      <P>Its meaning depends on the organism:</P>
      <Bullets
        items={[
          <><strong>Rubella IgG positive</strong> usually indicates immunity from vaccination or previous infection.</>,
          <><strong>CMV IgG positive</strong> indicates previous CMV infection, but it does not guarantee protection from reinfection, reactivation or congenital CMV.</>,
          <><strong>Toxoplasma IgG positive</strong> means infection occurred at some time; the result alone cannot date it.</>,
          <><strong>HSV IgG positive</strong> indicates previous infection, but HSV-1 antibody does not identify whether infection is oral or genital.</>,
        ]}
      />
      <P>The number printed beside IgG is not a direct measure of fetal damage.</P>

      <H2>What Does IgM Positive Mean?</H2>
      <P>IgM may appear early, but it is not a reliable clock.</P>
      <P>Possible explanations include:</P>
      <Bullets
        items={[
          "Recent primary infection.",
          "Persistent IgM from an older infection.",
          "Cross-reaction with another antibody.",
          "A false-positive laboratory result.",
          "Recurrent infection in some conditions.",
        ]}
      />
      <P>
        No major pregnancy decision should be based on one isolated IgM result. The next step may include repeat testing, comparison with an earlier sample, IgG avidity or reference-laboratory confirmation.
      </P>

      <H2>Rubella: IgG Is for Immunity—IgM Is Not a Screening Test</H2>
      <P>Rubella IgG is used to assess immunity.</P>
      <P>
        CDC advises that Rubella IgM should be ordered only when acute Rubella is clinically suspected, such as after a compatible illness or confirmed exposure. Routine IgM testing in an asymptomatic pregnant woman is not recommended because false-positive results occur. <Cite n="1" />
      </P>
      <P>A positive Rubella IgM in a well woman without exposure should be confirmed before it is labelled recent infection.</P>
      <P>IgG avidity may help distinguish a recent response from distant vaccination or infection, particularly early in pregnancy.</P>

      <H2>CMV: IgM Alone Is Especially Misleading</H2>
      <P>CMV is the clearest example of why “IgM positive” is not enough.</P>
      <P>
        ACOG notes that approximately 90% of positive CMV IgM results found during routine screening may be false positive. <Cite n="2" />
      </P>
      <P>Primary CMV infection is better supported by:</P>
      <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
        <Bullet>IgG changing from negative to positive on paired samples.</Bullet>
        <Bullet>A significant rise in IgG on appropriately timed samples.</Bullet>
        <Bullet>Positive IgM and IgG with low IgG avidity. <Cite n="2,3" /></Bullet>
      </ul>
      <P>Even then, maternal and fetal infection are different diagnoses.</P>
      <P>
        When fetal CMV testing is indicated, amniotic-fluid PCR is the preferred prenatal diagnostic test and should be timed appropriately—generally after 21 weeks and more than six weeks after maternal infection. <Cite n="3" />
      </P>

      <H2>Toxoplasma: IgM Can Remain Positive for Many Months</H2>
      <P>Toxoplasma IgG positive means infection occurred at some point.</P>
      <P>
        Toxoplasma IgM may remain detectable for as long as 18 months after a newly acquired infection. Commercial assays also differ in performance, so a positive IgM requires confirmation when timing matters. <Cite n="4" />
      </P>
      <P>
        IgG avidity may help, especially early in pregnancy. High avidity can support infection before conception, while low avidity does not by itself prove that infection occurred recently because low-avidity antibodies may persist.
      </P>
      <P>Reference-laboratory interpretation may combine IgG, IgM, avidity and other specialised antibody tests.</P>

      <H2>Herpes: Do Not Use HSV IgM</H2>
      <P>CDC does not recommend HSV IgM testing.</P>
      <P>
        HSV IgM is not type specific and may become positive during recurrent oral or genital herpes. <Cite n="5" />
      </P>
      <P>
        When active genital herpes is suspected, the useful test is usually PCR or culture from a fresh lesion. Type-specific IgG may help selected counselling, but routine HSV-2 screening of asymptomatic pregnant women is not recommended. <Cite n="5" />
      </P>
      <P>The presence of lesions or prodromal symptoms near labour matters more than a TORCH-panel HSV IgM result.</P>

      <H2>What Should Happen After a Positive Report?</H2>
      <P>Before interpreting the result, ask:</P>
      <Bullets
        items={[
          "Why was the test ordered?",
          "Was there a compatible illness or exposure?",
          "Which exact organism and assay were tested?",
          "Are both IgG and IgM available?",
          "Is an older sample available for comparison?",
          "Is IgG avidity useful for this organism and gestation?",
          "Does the result require confirmation at a reference laboratory?",
          "Are specialist ultrasound or organism-specific fetal tests indicated?",
        ]}
      />
      <P>Do not start treatment, recommend termination or provide false reassurance from one antibody result.</P>

      <div className="mt-10 bg-surfacealt/60 border-l-2 border-prestige p-6" data-testid="article-takeaway">
        <p className="text-[15px] text-navy leading-relaxed">
          <strong>Dr Tania’s takeaway:</strong> IgG positive is not the same as active infection. IgM positive is not the same as recent infection. Maternal infection is not the same as fetal infection. Rubella, CMV, Toxoplasma and Herpes each require their own interpretation pathway.
        </p>
      </div>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="evidence-base">
        Evidence Base
      </h2>
      <ol className="mt-6 space-y-5 list-none pl-0" data-testid="article-references">
        <li id="ref-1" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[1] Centers for Disease Control and Prevention. Serology Testing for Rubella. Updated June 10, 2024.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.cdc.gov/rubella/php/laboratories/serology-testing.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.cdc.gov/rubella/php/laboratories/serology-testing.html</a>
          </p>
        </li>
        <li id="ref-2" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[2] American College of Obstetricians and Gynecologists. Cytomegalovirus (CMV) in Pregnancy: Physician FAQ. Current online guidance.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.acog.org/clinical-information/physician-faqs/cytomegalovirus-in-pregnancy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.acog.org/clinical-information/physician-faqs/cytomegalovirus-in-pregnancy</a>
          </p>
        </li>
        <li id="ref-3" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[3] Society for Maternal-Fetal Medicine. Consult Series #39: Diagnosis and antenatal management of congenital cytomegalovirus infection.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://publications.smfm.org/publications/585-society-for-maternal-fetal-medicine-consult-series-39/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://publications.smfm.org/publications/585-society-for-maternal-fetal-medicine-consult-series-39/</a>
          </p>
        </li>
        <li id="ref-4" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[4] Centers for Disease Control and Prevention. DPDx—Toxoplasmosis: Laboratory Diagnosis. Updated 2025.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.cdc.gov/dpdx/toxoplasmosis/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.cdc.gov/dpdx/toxoplasmosis/index.html</a>
          </p>
        </li>
        <li id="ref-5" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[5] Centers for Disease Control and Prevention. Sexually Transmitted Infections Treatment Guidelines: Genital Herpes. Current online guidance.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.cdc.gov/std/treatment-guidelines/herpes.htm"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.cdc.gov/std/treatment-guidelines/herpes.htm</a>
          </p>
        </li>
      </ol>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="medical-disclaimer">
        Medical Disclaimer
      </h2>
      <P>
        This Article provides general education and does not replace patient-specific infectious-disease, fetal-medicine or obstetric assessment. Do not start treatment or make pregnancy decisions from an isolated IgG or IgM result. Seek prompt medical advice after a relevant exposure, fever with rash, new genital lesions, abnormal fetal findings or any concerning pregnancy symptom.
      </P>
    </article>
  );
}
